import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ClientWithBooking } from "../../lib/types/api-types";
import IntakeFormReadOnly from "../common/IntakeFormReadOnly";
import { useAuth } from "../../hooks/useSupabaseAuth";

const AdminClientView: React.FC = () => {
  const { id } = useParams();
  const [clientData, setClientData] = useState<ClientWithBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practitionerNotes, setPractitionerNotes] = useState<string>("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "overview" | "intake" | "purchases"
  >("overview");
  const [notesVisible, setNotesVisible] = useState(false);
  const { isAuthenticated } = useAuth();

  // const fetchComments = useCallback(async () => {
  //   try {
  //     if (!adminToken) return;
  //
  //     const response = await fetch(`/api/getComments?clientId=${id}`, {
  //       headers: {
  //         'Authorization': `Bearer ${adminToken}`
  //       }
  //     });
  //     if (!response.ok) {
  //       throw new Error('Failed to fetch comments');
  //     }
  //     const data = await response.json();
  //     setComments(data);
  //   } catch (err) {
  //     console.error("Error fetching comments:", err);
  //   }
  // }, [id, adminToken]);

  const fetchPractitionerNotes = useCallback(async () => {
    if (!id) return;

    try {
      const response = await fetch(`/api/admin/client-notes?clientId=${id}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPractitionerNotes(data.notes || "");
      }
    } catch (error) {
      console.error("Error fetching practitioner notes:", error);
    }
  }, [id]);

  const savePractitionerNotes = async () => {
    if (!id) return;

    setSavingNotes(true);
    try {
      const response = await fetch(`/api/admin/client-notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          clientId: id,
          notes: practitionerNotes,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save notes");
      }

      // Show success indicator
      setTimeout(() => setSavingNotes(false), 1000);
    } catch (error) {
      console.error("Error saving practitioner notes:", error);
      setSavingNotes(false);
    }
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!isAuthenticated) {
        setError("Admin not logged in");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/admin/client?id=${id}`, {
          credentials: "include",
        });

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: Failed to fetch client data`;
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = `${errorData.error}${errorData.details ? ` - ${errorData.details}` : ""}`;
            }
          } catch {
            // If JSON parsing fails, use the generic error message
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Validate that we have the expected data structure
        if (!data.personalInfo) {
          throw new Error(
            "Invalid client data structure: missing personalInfo"
          );
        }

        setClientData(data);
        fetchPractitionerNotes();
      } catch (err) {
        console.error("Error fetching client data:", err);
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      }
      setLoading(false);
    };

    if (id) {
      fetchClientData();
    }
  }, [id, isAuthenticated, fetchPractitionerNotes]);

  // const handleCommentSubmit = () => {
  //   fetchComments(); // Refresh comments after submitting
  // };

  if (loading) {
    return <div className="p-8">Loading client data...</div>;
  }

  if (error) {
    return <div className="p-8 text-red-500">Error: {error}</div>;
  }

  if (!clientData) {
    return <div className="p-8">No client data found.</div>;
  }

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Client Header */}
      <div className="bg-white shadow-lg rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-brand-stone-800">
              {clientData.personalInfo?.fullName || "Unknown Client"}
            </h2>
            <p className="text-brand-stone-600 mt-1">
              {clientData.personalInfo?.email} •{" "}
              {clientData.personalInfo?.phone}
            </p>
            <p className="text-sm text-brand-stone-500 mt-1">
              Submitted: {new Date(clientData.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="text-right">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                clientData.booking
                  ? "bg-green-100 text-green-800"
                  : "bg-orange-100 text-orange-800"
              }`}
            >
              {clientData.booking ? "Booked" : "Pending"}
            </span>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white shadow-lg rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-stone-600">Booking Status</p>
              <p className="text-lg font-semibold">
                {clientData.booking ? (
                  <span
                    className={`capitalize ${
                      clientData.booking.status === "confirmed"
                        ? "text-green-600"
                        : clientData.booking.status === "cancelled"
                          ? "text-red-600"
                          : "text-gray-600"
                    }`}
                  >
                    {clientData.booking.status}
                  </span>
                ) : (
                  <span className="text-orange-600">Not Booked</span>
                )}
              </p>
            </div>
            <div className="text-2xl">📅</div>
          </div>
        </div>
        <div className="bg-white shadow-lg rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-stone-600">Meeting Time</p>
              <p className="text-lg font-semibold">
                {clientData.booking?.timeSlot
                  ? new Date(clientData.booking.timeSlot).toLocaleString(
                      "en-US",
                      {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: "America/Chicago",
                      }
                    )
                  : "—"}
              </p>
            </div>
            <div className="text-2xl">⏰</div>
          </div>
        </div>
        <div className="bg-white shadow-lg rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-stone-600">Payment Status</p>
              <p className="text-lg font-semibold">
                {clientData.booking ? (
                  <span className="text-green-600">
                    Paid (${(clientData.booking.amount / 100).toFixed(2)}{" "}
                    {clientData.booking.currency.toUpperCase()})
                  </span>
                ) : (
                  <span className="text-orange-600">Pending</span>
                )}
              </p>
            </div>
            <div className="text-2xl">💳</div>
          </div>
        </div>
      </div>

      {/* Key Information Summary */}
      <div className="bg-white shadow-lg rounded-xl p-6">
        <h3 className="text-lg font-semibold text-brand-stone-800 mb-4">
          Key Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-brand-stone-700 mb-2">
              Main Health Concern
            </h4>
            <p className="text-brand-stone-600">
              {clientData.healthConcerns?.mainConcern || "Not specified"}
            </p>
          </div>
          <div>
            <h4 className="font-medium text-brand-stone-700 mb-2">
              Health Goals
            </h4>
            <p className="text-brand-stone-600">
              {clientData.healthConcerns?.healthGoals || "Not specified"}
            </p>
          </div>
          <div>
            <h4 className="font-medium text-brand-stone-700 mb-2">
              Commitment Level
            </h4>
            <p className="text-brand-stone-600">
              {clientData.healthConcerns?.commitmentScale
                ? `${clientData.healthConcerns.commitmentScale}/10`
                : "Not specified"}
            </p>
          </div>
          <div>
            <h4 className="font-medium text-brand-stone-700 mb-2">
              Biggest Obstacle
            </h4>
            <p className="text-brand-stone-600">
              {clientData.healthConcerns?.biggestObstacle || "Not specified"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntakeTab = () => (
    <div className="bg-white shadow-lg rounded-xl p-6">
      <IntakeFormReadOnly formData={clientData} showHeader={false} />
    </div>
  );

  const renderPurchasesTab = () => {
    // Combine all purchases into a single array
    const allPurchases = [
      ...(clientData.bookings || []),
      ...(clientData.contentPurchases || []),
    ].sort((a, b) => {
      const dateA = new Date(
        a.type === "appointment" ? a.createdAt : a.purchasedAt
      );
      const dateB = new Date(
        b.type === "appointment" ? b.createdAt : b.purchasedAt
      );
      return dateB.getTime() - dateA.getTime();
    });

    const formatCurrency = (amount: number, currency: string = "USD") => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(amount / 100);
    };

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const getStatusColor = (status: string) => {
      switch (status.toLowerCase()) {
        case "confirmed":
        case "completed":
        case "active":
          return "bg-green-100 text-green-800";
        case "cancelled":
        case "refunded":
          return "bg-red-100 text-red-800";
        case "pending":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <div className="bg-white shadow-lg rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-brand-stone-800">
            Purchase History
          </h3>
          <span className="text-sm text-brand-stone-600">
            {allPurchases.length}{" "}
            {allPurchases.length === 1 ? "purchase" : "purchases"}
          </span>
        </div>

        {allPurchases.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-brand-stone-200">
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Type
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Item
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Amount
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-brand-stone-700">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {allPurchases.map((purchase) => (
                  <tr
                    key={`${purchase.type}-${purchase.id}`}
                    className="border-b border-brand-stone-100 hover:bg-brand-stone-50"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">
                          {purchase.type === "appointment" ? "📅" : "📚"}
                        </span>
                        <span className="text-sm font-medium text-brand-stone-700 capitalize">
                          {purchase.type === "appointment"
                            ? "Appointment"
                            : "Content"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-brand-stone-800">
                        {purchase.type === "appointment" ? (
                          <div>
                            <div className="font-medium">
                              Consultation Session
                            </div>
                            <div className="text-xs text-brand-stone-600">
                              {formatDate(purchase.timeSlot)}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium">{purchase.title}</div>
                            <div className="text-xs text-brand-stone-600">
                              {purchase.category} • {purchase.accessCount} views
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-brand-stone-600">
                        {formatDate(
                          purchase.type === "appointment"
                            ? purchase.createdAt
                            : purchase.purchasedAt
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-brand-stone-800">
                        {formatCurrency(purchase.amount, purchase.currency)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(purchase.status)}`}
                      >
                        {purchase.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-xs text-brand-stone-600">
                        {purchase.type === "appointment" ? (
                          <div>
                            <div>
                              Session ID:{" "}
                              {purchase.stripeSessionId?.substring(0, 20)}...
                            </div>
                            {purchase.notes && (
                              <div className="mt-1">
                                Notes: {purchase.notes}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div>
                              Last accessed:{" "}
                              {purchase.lastAccessedAt
                                ? formatDate(purchase.lastAccessedAt)
                                : "Never"}
                            </div>
                            <div>
                              Session ID:{" "}
                              {purchase.stripeSessionId?.substring(0, 20)}...
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">💰</div>
            <p className="text-brand-stone-600">No purchases yet</p>
            <p className="text-sm text-brand-stone-500 mt-2">
              Client has submitted intake form but hasn't made any purchases
            </p>
          </div>
        )}

        {allPurchases.length > 0 && (
          <div className="mt-6 p-4 bg-brand-stone-50 rounded-lg">
            <div className="flex justify-between items-center text-sm">
              <span className="text-brand-stone-600">
                Total Revenue from this Client:
              </span>
              <span className="font-semibold text-brand-stone-800">
                {formatCurrency(
                  allPurchases.reduce(
                    (sum, purchase) => sum + purchase.amount,
                    0
                  ),
                  allPurchases[0]?.currency || "USD"
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderNotesPanel = () => (
    <div className="bg-white shadow-lg rounded-xl p-4 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-brand-stone-800">
          Practitioner Notes
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={savePractitionerNotes}
            disabled={savingNotes}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              savingNotes
                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                : "bg-brand-green-600 text-white hover:bg-brand-green-700"
            }`}
          >
            {savingNotes ? "Saving..." : "Save"}
          </button>
          <button
            onClick={() => setNotesVisible(false)}
            className="text-brand-stone-400 hover:text-brand-stone-600 transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
      <textarea
        value={practitionerNotes}
        onChange={(e) => setPractitionerNotes(e.target.value)}
        placeholder="Add your notes about this client here..."
        className="flex-1 p-3 border border-brand-stone-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent resize-none"
      />
      <p className="text-xs text-brand-stone-500 mt-2">
        These notes are private and only visible to practitioners
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <nav className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
            <Link
              to="/admin"
              className="text-brand-green-600 hover:text-brand-green-700 transition-colors truncate"
            >
              <span className="hidden sm:inline">Admin Portal</span>
              <span className="sm:hidden">Admin</span>
            </Link>
            <span className="text-brand-stone-400">›</span>
            <Link
              to="/admin"
              className="text-brand-green-600 hover:text-brand-green-700 transition-colors truncate"
            >
              <span className="hidden sm:inline">Client Management</span>
              <span className="sm:hidden">Clients</span>
            </Link>
            <span className="text-brand-stone-400">›</span>
            <span className="text-brand-stone-600 font-medium truncate">
              {clientData.personalInfo?.fullName || "Client Details"}
            </span>
          </nav>
        </div>
      </div>

      <div className="flex h-[calc(100vh-60px)] sm:h-[calc(100vh-80px)]">
        {/* Main Content Area */}
        <div
          className={`flex-1 transition-all duration-300 ${notesVisible ? "lg:mr-80" : ""}`}
        >
          <div className="p-4 sm:p-6 lg:p-8 h-full overflow-y-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-brand-green-800">
                Client Details
              </h1>
              <p className="text-brand-stone-600 mt-2 text-sm sm:text-base">
                Comprehensive view of client information and interaction history
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6">
              <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto">
                {[
                  { id: "overview", label: "Overview", icon: "📊" },
                  { id: "intake", label: "Intake Form", icon: "📝" },
                  { id: "purchases", label: "Purchases", icon: "💰" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? "bg-brand-green-100 text-brand-green-800 border-b-2 border-brand-green-600"
                        : "text-brand-stone-600 hover:text-brand-stone-800 hover:bg-brand-stone-50"
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">
                      {tab.id === "overview"
                        ? "Overview"
                        : tab.id === "intake"
                          ? "Form"
                          : "Purchases"}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "overview" && renderOverviewTab()}
              {activeTab === "intake" && renderIntakeTab()}
              {activeTab === "purchases" && renderPurchasesTab()}
            </div>
          </div>
        </div>

        {/* Notes Panel - Desktop */}
        {notesVisible && (
          <div className="hidden lg:block fixed right-0 top-16 sm:top-20 h-[calc(100vh-64px)] sm:h-[calc(100vh-80px)] w-80 bg-white shadow-xl border-l border-gray-200 z-10">
            <div className="h-full p-4">{renderNotesPanel()}</div>
          </div>
        )}
      </div>

      {/* Notes Panel - Mobile (Full Screen Modal) */}
      {notesVisible && (
        <div className="lg:hidden fixed inset-0 z-30 bg-white">
          <div className="h-full flex flex-col">
            <div className="bg-white shadow-sm border-b border-gray-200">
              <div className="px-4 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-brand-stone-800">
                  Practitioner Notes
                </h2>
                <button
                  onClick={() => setNotesVisible(false)}
                  className="text-brand-stone-400 hover:text-brand-stone-600 transition-colors p-2"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              <div className="h-full flex flex-col">
                <textarea
                  value={practitionerNotes}
                  onChange={(e) => setPractitionerNotes(e.target.value)}
                  placeholder="Add your notes about this client here..."
                  className="flex-1 p-4 border border-brand-stone-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent resize-none mb-4"
                />
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={savePractitionerNotes}
                    disabled={savingNotes}
                    className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                      savingNotes
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : "bg-brand-green-600 text-white hover:bg-brand-green-700"
                    }`}
                  >
                    {savingNotes ? "Saving..." : "Save Notes"}
                  </button>
                  <p className="text-xs text-brand-stone-500 text-center">
                    These notes are private and only visible to practitioners
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Notes Toggle Button */}
      <button
        onClick={() => setNotesVisible(!notesVisible)}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 p-3 sm:p-4 rounded-full shadow-lg transition-all duration-300 z-20 ${
          notesVisible
            ? "bg-brand-stone-500 text-white hover:bg-brand-stone-600"
            : "bg-brand-green-600 text-white hover:bg-brand-green-700"
        }`}
      >
        <span className="text-lg sm:text-xl">{notesVisible ? "✕" : "📋"}</span>
      </button>
    </div>
  );
};

export default AdminClientView;
